import { PricingTier, PaymentStatus } from '../types';
import { PAYMENT_RECEIVER_ADDRESS, WHITELISTED_WALLETS, NETWORK_CONFIG } from '../config/walletConfig';

// Define pricing tiers based on the requirements
export const pricingTiers: PricingTier[] = [
  {
    minNFTs: 0,
    maxNFTs: 10,
    price: "0",
    label: "Free"
  },
  {
    minNFTs: 11,
    maxNFTs: 100,
    price: "0.001",
    label: "Basic"
  },
  {
    minNFTs: 101,
    maxNFTs: 500,
    price: "0.003",
    label: "Standard"
  },
  {
    minNFTs: 501,
    maxNFTs: 1000,
    price: "0.01",
    label: "Premium"
  },
  {
    minNFTs: 1001,
    maxNFTs: null,
    price: "0.03",
    label: "Enterprise"
  }
];

// Get the appropriate pricing tier based on the number of NFTs
export const getPricingTier = (nftCount: number): PricingTier => {
  return pricingTiers.find(
    tier => nftCount >= tier.minNFTs && (tier.maxNFTs === null || nftCount <= tier.maxNFTs)
  ) || pricingTiers[pricingTiers.length - 1]; // Default to the highest tier if no match
};

// Check if a wallet address is whitelisted
export const isWalletWhitelisted = (address: string | null): boolean => {
  if (!address) return false;
  
  // Normaliser l'adresse en minuscules et supprimer les espaces
  const normalizedAddress = address.toLowerCase().trim();
  
  // Log pour le débogage
  console.log("Vérification de whitelist pour l'adresse:", normalizedAddress);
  
  // Vérifier si l'adresse normalisée est dans la liste
  const isWhitelisted = WHITELISTED_WALLETS.some(whitelistedAddress => 
    whitelistedAddress.toLowerCase().trim() === normalizedAddress
  );
  
  console.log("Résultat de la vérification:", isWhitelisted);
  
  return isWhitelisted;
};

// Check if payment is required based on the number of NFTs and wallet address
export const isPaymentRequired = (nftCount: number, walletAddress: string | null = null): boolean => {
  // Si le portefeuille est whitelisté, aucun paiement n'est requis
  const isWhitelisted = isWalletWhitelisted(walletAddress);
  console.log("isPaymentRequired - walletAddress:", walletAddress, "isWhitelisted:", isWhitelisted);
  
  if (isWhitelisted) {
    console.log("Portefeuille whitelisté, aucun paiement requis");
    return false;
  }
  
  // Sinon, vérifier selon le nombre de NFTs
  const tier = getPricingTier(nftCount);
  const paymentRequired = tier.price !== "0";
  console.log("Vérification basée sur le tier:", tier.label, "prix:", tier.price, "paiement requis:", paymentRequired);
  
  return paymentRequired;
};

// Check if any Ethereum provider is available (MetaMask, Coinbase Wallet, etc.)
export const isWalletProviderAvailable = (): boolean => {
  // Sur mobile avec un navigateur standard, nous voulons afficher les boutons pour ouvrir les apps
  if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && 
      !window.ethereum && 
      !window.coinbaseWalletExtension && 
      !window.navigator.userAgent.match(/Coinbase\/[0-9]/) && 
      !window.navigator.userAgent.match(/MetaMask\/[0-9]/)) {
    return false;
  }
  
  return typeof window !== 'undefined' && 
    (window.ethereum !== undefined || 
     window.coinbaseWalletExtension !== undefined || 
     !!window.navigator.userAgent.match(/Coinbase\/[0-9]/) ||
     !!window.navigator.userAgent.match(/MetaMask\/[0-9]/));
};

// Check if MetaMask is installed
export const isMetaMaskInstalled = (): boolean => {
  return typeof window !== 'undefined' && 
    window.ethereum !== undefined && 
    (window.ethereum.isMetaMask === true || 
     !!window.navigator.userAgent.match(/MetaMask\/[0-9]/));
};

// Check if Coinbase Wallet is available
export const isCoinbaseWalletAvailable = (): boolean => {
  return typeof window !== 'undefined' && 
    (window.coinbaseWalletExtension !== undefined || 
     !!window.navigator.userAgent.match(/Coinbase\/[0-9]/));
};

// Get wallet provider name
export const getWalletProviderName = (): string => {
  if (isMetaMaskInstalled()) {
    return 'MetaMask';
  } else if (isCoinbaseWalletAvailable()) {
    return 'Coinbase Wallet';
  } else if (typeof window !== 'undefined' && window.ethereum !== undefined) {
    return 'Ethereum Wallet';
  }
  return 'Wallet';
};

// Connect to available wallet provider
export const connectToWallet = async (): Promise<string | null> => {
  try {
    if (!isWalletProviderAvailable()) {
      // Si aucun portefeuille n'est disponible, ne pas afficher de confirmation
      // Laisser le composant PaymentModal gérer l'affichage des options
      throw new Error("No wallet provider available");
    }

    // Utiliser le provider disponible (ethereum global, coinbaseWalletExtension, etc.)
    const provider = window.ethereum || window.coinbaseWalletExtension;
    
    if (!provider) {
      // Si nous sommes sur mobile, mais que nous n'avons pas détecté de provider
      // Laisser le composant PaymentModal gérer l'affichage des options
      throw new Error("No wallet provider available");
    }

    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    return accounts[0] || null;
  } catch (error) {
    console.error("Error connecting to wallet:", error);
    return null;
  }
};

// Alias for backward compatibility
export const connectToMetaMask = connectToWallet;

// Check if the current network is Base Mainnet
export const isBaseMainnet = async (): Promise<boolean> => {
  try {
    if (!isWalletProviderAvailable()) {
      return false;
    }

    const provider = window.ethereum || window.coinbaseWalletExtension;
    if (!provider) return false;

    const chainId = await provider.request({ method: 'eth_chainId' });
    return chainId === NETWORK_CONFIG.chainId; // Base Mainnet Chain ID
  } catch (error) {
    console.error("Error checking network:", error);
    return false;
  }
};

// Switch to Base Mainnet network
export const switchToBaseMainnet = async (): Promise<boolean> => {
  try {
    if (!isWalletProviderAvailable()) {
      return false;
    }

    const provider = window.ethereum || window.coinbaseWalletExtension;
    if (!provider) return false;

    try {
      // Try to switch to the Base Mainnet network
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: NETWORK_CONFIG.chainId }], // Base Mainnet Chain ID
      });
      return true;
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to the wallet
      if (switchError.code === 4902) {
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: NETWORK_CONFIG.chainId,
                chainName: NETWORK_CONFIG.chainName,
                nativeCurrency: NETWORK_CONFIG.nativeCurrency,
                rpcUrls: NETWORK_CONFIG.rpcUrls,
                blockExplorerUrls: NETWORK_CONFIG.blockExplorerUrls
              },
            ],
          });
          return true;
        } catch (addError) {
          console.error("Error adding network:", addError);
          return false;
        }
      }
      console.error("Error switching network:", switchError);
      return false;
    }
  } catch (error) {
    console.error("Error switching to Base Mainnet:", error);
    return false;
  }
};

// Process payment using wallet
export const processPayment = async (
  amount: string,
  toAddress: string = PAYMENT_RECEIVER_ADDRESS, // Utilise l'adresse de destination du fichier de configuration
  onStatusChange: (status: PaymentStatus) => void
): Promise<string | null> => {
  try {
    if (!isWalletProviderAvailable()) {
      throw new Error("No wallet provider available");
    }

    const provider = window.ethereum || window.coinbaseWalletExtension;
    if (!provider) {
      throw new Error("No wallet provider available");
    }

    onStatusChange(PaymentStatus.PROCESSING);

    // Get the current account
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    const fromAddress = accounts[0];

    // Convert amount to wei (1 ETH = 10^18 wei)
    const amountInWei = `0x${(Number(amount) * 1e18).toString(16)}`;

    // Send the transaction
    const txHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [
        {
          from: fromAddress,
          to: toAddress,
          value: amountInWei,
          gas: '0x5208', // 21000 gas in hex
        },
      ],
    });

    console.log("Transaction sent:", txHash);
    onStatusChange(PaymentStatus.COMPLETED);
    return txHash as string;
  } catch (error) {
    console.error("Payment error:", error);
    onStatusChange(PaymentStatus.FAILED);
    throw error;
  }
};

// Add TypeScript declarations for window.ethereum and window.coinbaseWalletExtension
declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      request: (request: { method: string; params?: any[] }) => Promise<any>;
      on: (eventName: string, callback: (...args: any[]) => void) => void;
    };
    coinbaseWalletExtension?: {
      request: (request: { method: string; params?: any[] }) => Promise<any>;
      on: (eventName: string, callback: (...args: any[]) => void) => void;
    };
  }
} 